import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeartPulse,
  faChevronRight,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { capitaliseFirstLetter } from "../../utils";
import { useNavigate } from "react-router-dom";
const MembershipCard = ({ subscription, showTitle = false }) => {
  const navigate = useNavigate();
  const hasAccess = (() => {
    if (!subscription) return false;
    // Active, Paused, or cancelling all still provide access
    if (
      ["active", "paused", "cancelling", "trialing"].includes(
        subscription.stripe_status,
      )
    ) {
      return true;
    }

    // cancelled status: Check if the end date is in the future
    if (subscription.stripe_status === "cancelled" && subscription.ends_at) {
      return new Date(subscription.ends_at) > new Date();
    }

    return false;
  })();

  const plan = subscription?.plan;
  // 2. Updated status styles
  const statusClasses = {
    active: "bg-green-100 text-green-700",
    cancelling: "bg-yellow-100 text-yellow-700", // New style for cancelling
    trialing: "bg-yellow-100 text-yellow-700", // New style for cancelling
    cancelled: "bg-red-100 text-red-700",
    paused: "bg-orange-100 text-orange-700",
  };

  // 3. Helper to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="py-2 pb-4">
      {showTitle && (
        <div className="text-md font-bold mb-4">Manage Membership</div>
      )}

      {hasAccess ? (
        <button
          type="button"
          onClick={() => navigate("/my-membership")}
          className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-5 text-left transition-all hover:border-hub-primary hover:shadow-sm"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#F3EDF7]">
              <FontAwesomeIcon
                icon={faHeartPulse}
                className="text-hub-primary text-2xl"
              />
            </div>
            <div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-900">
                  {plan?.name}
                </h3>

                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    statusClasses[subscription?.stripe_status] ??
                    "bg-gray-100 text-gray-700"
                  }`}
                >
                  {capitaliseFirstLetter(subscription.stripe_status)}
                </span>

                {subscription.stripe_status === "trialing" && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="text-slate-500"
                    />
                    <span>
                      Trial ends on{" "}
                      <span className="font-semibold">
                        {formatDate(subscription.trial_ends_at)}
                      </span>
                    </span>
                  </span>
                )}

                {subscription.stripe_status === "active" &&
                  subscription.ends_at && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-slate-500"
                      />
                      <span>
                        Cancels on{" "}
                        <span className="font-semibold">
                          {formatDate(subscription.ends_at)}
                        </span>
                      </span>
                    </span>
                  )}
              </div>

              <p className="mt-1 text-sm text-slate-500">
                View your plan, billing and upgrade options.
              </p>
              <p className="mt-1 text-sm font-semibold text-hub-primary">
                Use the <span className="font-extrabold">School Wellbeing</span>{" "}
                app for member features and services. Use this portal to manage
                your membership, billing and upgrades.
              </p>
            </div>
          </div>
          <FontAwesomeIcon icon={faChevronRight} className="text-slate-400" />
        </button>
      ) : (
        <div
          className="w-full rounded-2xl border border-[#407cb9bd] bg-[#407cb9bd] p-4 text-left shadow-sm sm:p-5 cursor-pointer"
          onClick={() => navigate("/my-membership")}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-start gap-3 lg:items-center lg:gap-4">
              {/* <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#d9caeb] bg-white/80 text-[#5d3b8c] shadow-sm lg:h-20 lg:w-20">
                <FontAwesomeIcon icon={faHeartPulse} className="text-[32px]" />
              </div> */}
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-tight text-white lg:text-xl">
                  Unlock Even More Wellbeing Support with SWB+
                </h3>
                <p className="mt-1 text-sm text-white">
                  Benefit from even more tools, resources and support for your
                  wellbeing journey. Enjoy personalised insights, more health
                  resources and expert guidance.
                </p>
                <p className="mt-3 flex flex-wrap items-center gap-x-1 rounded-md bg-[#eef9f1] px-2.5 py-1.5 text-xs font-medium text-[#267443] max-w-[500px] w-full">
                  <span className="font-bold">First month free</span>
                  when you sign up before 31 December 2026. Then £2.50/month.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="w-full shrink-0 rounded-xl border border-[#6d4aa6] bg-white px-4 py-2.5 text-sm font-medium text-[#4a2d6d] transition-all hover:bg-[#f6f0ff] lg:w-auto"
            >
              <span className="inline-flex w-full items-center justify-center gap-2">
                Click to Learn More
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipCard;
